import React, { useEffect, useState } from "react";
import { formatDate } from "../../../utils/dateUtils";
import ShopMedia from "./ShopMedia";
import ShopStatusActions from "../StatusActions/ShopStatusActions";

export default function ShopDetails({
  shop,
  onStatusChange,
  onUpdate,
  onUpdateMedia,
  userId,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: shop.name || "",
    buildingNo: shop.buildingNo || "",
    street: shop.street || "",
    landmark: shop.landmark || "",
    city: shop.city || "",
    state: shop.state || "",
    pincode: shop.pincode || "",
    contactPhone: shop.contactPhone || "",
    contactEmail: shop.contactEmail || "",
    shopPhone: shop.shopPhone || "",
    openingTime: shop.openingTime || "",
    closingTime: shop.closingTime || "",
    businessDays: shop.businessDays || [],
    gst: shop.gst || "",
    sellerType: shop.sellerType || "UNREGISTERED",
  });

  // When active shop changes while editing, sync the form to the newly selected shop
  useEffect(() => {
    setEditData({
      name: shop.name || "",
      buildingNo: shop.buildingNo || "",
      street: shop.street || "",
      landmark: shop.landmark || "",
      city: shop.city || "",
      state: shop.state || "",
      pincode: shop.pincode || "",
      contactPhone: shop.contactPhone || "",
      contactEmail: shop.contactEmail || "",
      shopPhone: shop.shopPhone || "",
      openingTime: shop.openingTime || "",
      closingTime: shop.closingTime || "",
      businessDays: shop.businessDays || [],
      gst: shop.gst || "",
      sellerType: shop.sellerType || "UNREGISTERED",
    });
    // Intentionally do not change isEditing so edit mode persists if it was on
  }, [shop]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      name: shop.name || "",
      buildingNo: shop.buildingNo || "",
      street: shop.street || "",
      landmark: shop.landmark || "",
      city: shop.city || "",
      state: shop.state || "",
      pincode: shop.pincode || "",
      contactPhone: shop.contactPhone || "",
      contactEmail: shop.contactEmail || "",
      shopPhone: shop.shopPhone || "",
      openingTime: shop.openingTime || "",
      closingTime: shop.closingTime || "",
      businessDays: shop.businessDays || [],
      gst: shop.gst || "",
      sellerType: shop.sellerType || "UNREGISTERED",
    });
  };

  const handleSave = async () => {
    try {
      await onUpdate(editData);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update shop details:", error);
    }
  };

  const handleMediaUpload = async (event, type) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    try {
      // Expect API to accept FormData for files or URLs array. We'll support both by sending URLs list if provided.
      // Build a payload that backend understands; here we send arrays keyed by shopImages/shopVideos.
      const payload =
        type === "images" ? { shopImages: files } : { shopVideos: files };
      await onUpdateMedia(payload);
    } catch (error) {
      console.error("Failed to update shop media:", error);
    } finally {
      event.target.value = "";
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      name: shop.name || "",
      buildingNo: shop.buildingNo || "",
      street: shop.street || "",
      landmark: shop.landmark || "",
      city: shop.city || "",
      state: shop.state || "",
      pincode: shop.pincode || "",
      contactPhone: shop.contactPhone || "",
      contactEmail: shop.contactEmail || "",
      shopPhone: shop.shopPhone || "",
      openingTime: shop.openingTime || "",
      closingTime: shop.closingTime || "",
      businessDays: shop.businessDays || [],
      gst: shop.gst || "",
      sellerType: shop.sellerType || "UNREGISTERED",
    });
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBusinessDayToggle = (day) => {
    setEditData((prev) => ({
      ...prev,
      businessDays: prev.businessDays.includes(day)
        ? prev.businessDays.filter((d) => d !== day)
        : [...prev.businessDays, day],
    }));
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-800">Edit Shop Details</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Save Changes
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">
              Basic Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Shop Name
                </label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  value={editData.gst}
                  onChange={(e) => handleInputChange("gst", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Seller Type
                </label>
                <select
                  value={editData.sellerType}
                  onChange={(e) =>
                    handleInputChange("sellerType", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="UNREGISTERED">Unregistered</option>
                  <option value="COMPOSITION">Composition</option>
                  <option value="REGULAR">Regular</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">
              Address
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Building No
                </label>
                <input
                  type="text"
                  value={editData.buildingNo}
                  onChange={(e) =>
                    handleInputChange("buildingNo", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Street
                </label>
                <input
                  type="text"
                  value={editData.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Landmark
                </label>
                <input
                  type="text"
                  value={editData.landmark}
                  onChange={(e) =>
                    handleInputChange("landmark", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={editData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={editData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  value={editData.pincode}
                  onChange={(e) => handleInputChange("pincode", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">
              Contact Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={editData.contactPhone}
                  onChange={(e) =>
                    handleInputChange("contactPhone", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Shop Phone
                </label>
                <input
                  type="tel"
                  value={editData.shopPhone}
                  onChange={(e) =>
                    handleInputChange("shopPhone", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={editData.contactEmail}
                  onChange={(e) =>
                    handleInputChange("contactEmail", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3">
              Business Hours
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Opening Time
                </label>
                <input
                  type="time"
                  value={editData.openingTime}
                  onChange={(e) =>
                    handleInputChange("openingTime", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Closing Time
                </label>
                <input
                  type="time"
                  value={editData.closingTime}
                  onChange={(e) =>
                    handleInputChange("closingTime", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Business Days
              </label>
              <div className="flex flex-wrap gap-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day, index) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleBusinessDayToggle(index)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        editData.businessDays.includes(index)
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {day}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Media Uploads */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-sm text-gray-700 mb-3">
                Media
              </h4>
              {/* Show currently uploaded media while editing */}
              <div className="mb-3">
                <ShopMedia images={shop.shopImages} videos={shop.shopVideos} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Update Shop Images
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleMediaUpload(e, "images")}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Update Shop Videos
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={(e) => handleMediaUpload(e, "videos")}
                    className="w-full text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg text-gray-800">{shop.name}</h3>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold mt-2 inline-block
            ${
              shop.status === "APPROVED"
                ? "bg-green-200 text-green-700"
                : shop.status === "REJECTED"
                ? "bg-red-200 text-red-700"
                : shop.status === "HOLD"
                ? "bg-yellow-200 text-yellow-700"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {shop.status}
          </span>
        </div>
        <button
          onClick={handleEdit}
          className="px-3 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium"
        >
          ✏️ Edit Shop
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {/* Removed View Offers and Manage Products buttons */}
      </div>

      <div className="mt-4 space-y-4">
        {/* Address Section */}
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Address</h4>
          <div className="text-sm text-gray-700 space-y-1">
            {shop.buildingNo && <p>{shop.buildingNo}</p>}
            <p>{shop.street}</p>
            {shop.landmark && <p>Near {shop.landmark}</p>}
            <p>
              {shop.city}, {shop.state} - {shop.pincode}
            </p>
            {shop.latitude && shop.longitude && (
              <div className="text-xs text-gray-500 mt-2">
                <span className="mr-4">Lat: {shop.latitude.toFixed(6)}</span>
                <span>Long: {shop.longitude.toFixed(6)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Shop Details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold text-gray-600">Created:</span>
            <p className="text-gray-800">{formatDate(shop.createdAt)}</p>
          </div>
          <div>
            <span className="font-semibold text-gray-600">Last Updated:</span>
            <p className="text-gray-800">{formatDate(shop.updatedAt)}</p>
          </div>
          <div>
            <span className="font-semibold text-gray-600">Verified:</span>
            <p className="text-gray-800">{shop.isVerified ? "Yes" : "No"}</p>
          </div>
          {shop.verificationDate && (
            <div>
              <span className="font-semibold text-gray-600">
                Verification Date:
              </span>
              <p className="text-gray-800">
                {formatDate(shop.verificationDate)}
              </p>
            </div>
          )}
        </div>

        {/* Business Hours */}
        {(shop.openingTime ||
          shop.closingTime ||
          shop.businessDays?.length > 0) && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Business Hours
            </h4>
            <div className="text-sm text-gray-700 space-y-1">
              {shop.openingTime && shop.closingTime && (
                <p>
                  Time: {shop.openingTime} - {shop.closingTime}
                </p>
              )}
              {shop.businessDays?.length > 0 && (
                <p>
                  Days:{" "}
                  {shop.businessDays
                    .map(
                      (day) =>
                        ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day]
                    )
                    .join(", ")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Contact Information */}
        {(shop.contactPhone || shop.contactEmail || shop.shopPhone) && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Contact Information
            </h4>
            <div className="text-sm text-gray-700 space-y-1">
              {shop.contactPhone && <p>Phone: {shop.contactPhone}</p>}
              {shop.shopPhone && <p>Shop Phone: {shop.shopPhone}</p>}
              {shop.contactEmail && <p>Email: {shop.contactEmail}</p>}
            </div>
          </div>
        )}

        {/* GST and Seller Type */}
        {(shop.gst || shop.sellerType) && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Business Details
            </h4>
            <div className="text-sm text-gray-700 space-y-1">
              {shop.gst && <p>GST: {shop.gst}</p>}
              {shop.sellerType && <p>Seller Type: {shop.sellerType}</p>}
            </div>
          </div>
        )}

        {/* Delivery Information */}
        {(shop.baseDeliveryFee || shop.maxDeliveryDistanceKm) && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Delivery Information
            </h4>
            <div className="text-sm text-gray-700 space-y-1">
              {shop.baseDeliveryFee > 0 && (
                <p>Base Fee: ₹{shop.baseDeliveryFee}</p>
              )}
              {shop.baseDeliveryDistanceKm > 0 && (
                <p>Base Distance: {shop.baseDeliveryDistanceKm}km</p>
              )}
              {shop.perKmDeliveryFee > 0 && (
                <p>Per KM Fee: ₹{shop.perKmDeliveryFee}</p>
              )}
              {shop.maxDeliveryDistanceKm > 0 && (
                <p>Max Distance: {shop.maxDeliveryDistanceKm}km</p>
              )}
            </div>
          </div>
        )}

        {/* Social Links */}
        {shop.socialLinks &&
          Object.values(shop.socialLinks).some((link) => link) && (
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2">
                Social Links
              </h4>
              <div className="text-sm text-gray-700 space-y-1">
                {shop.socialLinks.website && (
                  <p>
                    Website:{" "}
                    <a
                      href={shop.socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {shop.socialLinks.website}
                    </a>
                  </p>
                )}
                {shop.socialLinks.facebook && (
                  <p>
                    Facebook:{" "}
                    <a
                      href={shop.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {shop.socialLinks.facebook}
                    </a>
                  </p>
                )}
                {shop.socialLinks.instagram && (
                  <p>
                    Instagram:{" "}
                    <a
                      href={shop.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {shop.socialLinks.instagram}
                    </a>
                  </p>
                )}
                {shop.socialLinks.twitter && (
                  <p>
                    Twitter:{" "}
                    <a
                      href={shop.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {shop.socialLinks.twitter}
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Shop Media */}
      <ShopMedia images={shop.shopImages} videos={shop.shopVideos} />

      {/* Status Actions */}
      <ShopStatusActions status={shop.status} onChange={onStatusChange} />
    </div>
  );
}
